import { apiClient } from '~api/client';
import type { BlocksResponse, HardnessDomainResponse, HardnessTraceResponse } from './hardness.types';

type BlocksQueryParams = {
  /** Filter blocks by blast name */
  blast_name?: string;
  /** Optionally filter blocks by model name */
  model_name?: string;
  /** Buffer in m around the blast within which to query blocks */
  buffer?: number;
  /** Bench height determines the horizontal intersection plane for the block query (default 10m)*/
  bench_height?: string;
};

type HardnessRuleQueryParams = {
  /** Strand property from the block  */
  strand?: string;
  /** Geology property from the block  */
  geology?: string;
  /** Mineralisation property from the block  */
  mineralisation?: number;
  /** Depth property from the block  */
  depth?: number;
};

const hardnessService = {
  /**
   * Fetch hardness domain data for a specific blast
   * @param blastName - Name of the blast
   * @returns Promise resolving to hardness domain data
   */
  getHardnessDomainByBlast: async (blastName: string): Promise<HardnessDomainResponse> => {
    const { data } = await apiClient.get<HardnessDomainResponse>('/geology/hardness_domains', {
      params: { blast_name: blastName, crs: 'local' },
    });
    return data;
  },
  /**
   * Fetch Foundry block model data
   * @param params - Query parameters identifying which blocks to retrieve
   * @returns Promise resolving to blocks data
   */
  getBlocks: async (params?: BlocksQueryParams): Promise<BlocksResponse> => {
    const { blast_name, model_name, buffer, bench_height } = params || {};
    const { data } = await apiClient.get<BlocksResponse>('/geology/blocks', {
      params: { blast_name, model_name, buffer, bench_height, bench_level: 'all', crs: 'local' },
    });
    return data;
  },
  /**
   * @param params - Query parameters to determine the hardness rule applied
   * @returns Promise resolving to the hardness rule response based on the provided parameters
   */
  getHardnessRuleForBlock: async (params?: HardnessRuleQueryParams): Promise<HardnessTraceResponse> => {
    const { strand, geology, mineralisation, depth } = params || {};
    const { data } = await apiClient.get<HardnessTraceResponse>('/geology/hardness', {
      params: { strand, geology, mineralisation, depth },
    });
    return data;
  },
};

export type { BlocksQueryParams, HardnessRuleQueryParams };
export { hardnessService };
